import { useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Ban, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * "Anular recibo" — voids a ticket wherever it's listed (monthly documents
 * panel, a student's payment history). Same action the pago-aplazado card
 * offers per plazo; the backend reverts the linked plazo to pending when the
 * receipt belongs to one.
 *
 * `item` needs: { id, invoice_number, document_kind, is_manual, status }.
 */
export default function CancelTicketButton({ item, onDone, size = "sm" }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const isTicket = (item?.document_kind || "invoice") === "ticket";
  const alreadyCancelled = item?.status === "cancelled";
  // Only live tickets can be voided: invoices/credit notes go through the
  // devolución flow, and a cancelled one can't be cancelled twice.
  if (!isTicket || alreadyCancelled) return null;

  const isOnline = !item?.is_manual;

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/tx/${item.id}/cancel`);
      if (data?.installment_reverted) {
        toast.success("Recibo anulado — el plazo vuelve a estar pendiente de cobro");
      } else {
        toast.success("Recibo anulado");
      }
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo anular el recibo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size={size}
        onClick={() => setOpen(true)}
        title="Anular recibo"
        className="text-red-600 hover:bg-red-50"
        data-testid={`cancel-ticket-${item.id}`}
      >
        <Ban className="w-3.5 h-3.5" />
      </Button>

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent data-testid="cancel-ticket-dialog">
          <DialogHeader>
            <DialogTitle>¿Anular el recibo {item.invoice_number || ""}?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  El recibo quedará marcado como <strong>anulado</strong> y se registrará quién lo
                  ha anulado y cuándo. No se borra: sigue constando en el histórico como documento
                  fiscal.
                </p>
                <p>
                  Si el recibo corresponde a un plazo de pago aplazado, ese plazo volverá a
                  <strong> pendiente</strong> y se podrá volver a cobrar.
                </p>
                {isOnline && (
                  <p className="text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-3">
                    <strong>Atención:</strong> este cobro se hizo online. Anular el recibo{" "}
                    <strong>no devuelve el dinero al cliente</strong> — el reembolso hay que
                    hacerlo aparte desde Stripe.
                  </p>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Volver
            </Button>
            <Button
              onClick={submit}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="cancel-ticket-confirm"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ban className="w-4 h-4 mr-2" />}
              Sí, anular recibo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
